import React, { useState } from 'react'
import { X, Sparkles, Loader, RefreshCw, Check } from 'lucide-react'
import DOMPurify from 'dompurify'

interface AIContentGeneratorProps {
  isOpen: boolean
  onClose: () => void
  contentType: 'features' | 'subprocessors' | 'badges' | 'privacy-policy' | 'terms' | 'dmca-submit' | 'dmca-counter'
  onApprove: (content: any) => void
  existingContent?: any
}

const AIContentGenerator: React.FC<AIContentGeneratorProps> = ({
  isOpen,
  onClose,
  contentType,
  onApprove,
  existingContent
}) => {
  const [prompt, setPrompt] = useState('')
  const [generating, setGenerating] = useState(false)
  const [generatedContent, setGeneratedContent] = useState<any>(null)
  const [error, setError] = useState('')

  if (!isOpen) return null

  const getPlaceholder = () => {
    switch (contentType) {
      case 'features':
        return 'Describe the security/compliance features you want to generate (e.g., "Generate 5 security features about encryption, access control, and monitoring")'
      case 'subprocessors':
        return 'List vendor names or describe the subprocessors (e.g., "AWS, Google Cloud, Stripe, SendGrid")'
      case 'badges':
        return 'Describe the compliance badges you want to generate (e.g., "Generate badges for SOC 2, GDPR, CCPA, and ISO 27001")'
      case 'privacy-policy':
        return 'Describe what to include or modify in the privacy policy'
      case 'terms':
        return 'Describe what to include or modify in the terms of service'
      case 'dmca-submit':
      case 'dmca-counter':
        return 'Describe what to include or modify in the DMCA policy'
      default:
        return 'Describe what you want to generate'
    }
  }

  const getTitle = () => {
    switch (contentType) {
      case 'features':
        return 'Generate Security & Compliance Features with AI'
      case 'subprocessors':
        return 'Generate Subprocessor List with AI'
      case 'badges':
        return 'Generate Compliance Badges with AI'
      case 'privacy-policy':
        return 'Generate/Edit Privacy Policy with AI'
      case 'terms':
        return 'Generate/Edit Terms of Service with AI'
      case 'dmca-submit':
        return 'Generate/Edit DMCA Submission Policy with AI'
      case 'dmca-counter':
        return 'Generate/Edit DMCA Counter Notice Policy with AI'
      default:
        return 'Generate Content with AI'
    }
  }

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      setError('Please enter a description or prompt')
      return
    }

    setGenerating(true)
    setError('')

    try {
      const response = await fetch('/api/ai-generate-content', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contentType,
          prompt: prompt.trim(),
          existingContent
        })
      })

      if (!response.ok) {
        // Try to get the error message from the response
        const errorData = await response.json().catch(() => null)
        const errorMessage = errorData?.message || errorData?.error || 'Failed to generate content'
        throw new Error(errorMessage)
      }

      const data = await response.json()
      setGeneratedContent(data.content)
    } catch (err) {
      console.error('Generation error:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate content. Please try again.'
      setError(errorMessage)
    } finally {
      setGenerating(false)
    }
  }

  const handleApprove = () => {
    if (generatedContent) {
      onApprove(generatedContent)
      onClose()
      // Reset state
      setPrompt('')
      setGeneratedContent(null)
      setError('')
    }
  }

  const handleRegenerate = () => {
    setGeneratedContent(null)
    handleGenerate()
  }

  const renderPreview = () => {
    if (!generatedContent) return null

    switch (contentType) {
      case 'features':
        return (
          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-700">Generated Features:</p>
            {Array.isArray(generatedContent) ? (
              generatedContent.map((feature, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-white">
                  <div className="flex items-start space-x-3">
                    <div className="text-2xl">{feature.icon || '🔒'}</div>
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">{feature.title}</h4>
                      <p className="text-sm text-gray-600 mt-1">{feature.description}</p>
                      {feature.category && (
                        <span className="inline-block mt-2 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                          {feature.category}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <pre className="text-sm bg-gray-50 p-3 rounded-lg overflow-auto max-h-96">
                {JSON.stringify(generatedContent, null, 2)}
              </pre>
            )}
          </div>
        )

      case 'subprocessors':
        return (
          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-700">Generated Subprocessors:</p>
            {Array.isArray(generatedContent) ? (
              <div className="border border-gray-200 rounded-lg divide-y">
                {generatedContent.map((subprocessor, idx) => (
                  <div key={idx} className="p-4 bg-white">
                    <h4 className="font-medium text-gray-900">{subprocessor.name}</h4>
                    <p className="text-sm text-gray-600 mt-1">{subprocessor.service}</p>
                    {subprocessor.location && (
                      <p className="text-xs text-gray-500 mt-1">📍 {subprocessor.location}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <pre className="text-sm bg-gray-50 p-3 rounded-lg overflow-auto max-h-96">
                {JSON.stringify(generatedContent, null, 2)}
              </pre>
            )}
          </div>
        )

      case 'badges':
        return (
          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-700">Generated Compliance Badges:</p>
            {Array.isArray(generatedContent) ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {generatedContent.map((badge, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-white">
                    <div className="flex items-start space-x-3">
                      <div className="text-2xl">{badge.icon || '✓'}</div>
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{badge.name}</h4>
                        {badge.description && (
                          <p className="text-sm text-gray-600 mt-1">{badge.description}</p>
                        )}
                        {badge.status && (
                          <span className="inline-block mt-2 px-2 py-1 bg-green-100 text-green-700 text-xs rounded">
                            {badge.status}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <pre className="text-sm bg-gray-50 p-3 rounded-lg overflow-auto max-h-96">
                {JSON.stringify(generatedContent, null, 2)}
              </pre>
            )}
          </div>
        )

      default:
        return (
          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-700">Generated Content:</p>
            <div className="prose prose-sm max-w-none bg-gray-50 p-4 rounded-lg max-h-96 overflow-auto">
              {typeof generatedContent === 'string' ? (
                // SECURITY FIX: Sanitize AI-generated HTML with DOMPurify to prevent XSS attacks
                // This prevents malicious scripts from executing if AI generates harmful content
                <div dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(generatedContent, {
                    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'ul', 'ol', 'li', 'a', 'code', 'pre'],
                    ALLOWED_ATTR: ['href', 'target', 'rel'],
                    ALLOW_DATA_ATTR: false
                  })
                }} />
              ) : (
                <pre>{JSON.stringify(generatedContent, null, 2)}</pre>
              )}
            </div>
          </div>
        )
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-full sm:max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Sparkles className="h-5 w-5 text-purple-600" />
            <h2 className="text-xl font-semibold text-gray-900">{getTitle()}</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Prompt Input */}
          {!generatedContent && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                What would you like to generate?
              </label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={getPlaceholder()}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                rows={6}
                disabled={generating}
              />
              {error && (
                <p className="mt-2 text-sm text-red-600">{error}</p>
              )}
            </div>
          )}

          {/* Generated Content Preview */}
          {generatedContent && renderPreview()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          {!generatedContent ? (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
                disabled={generating}
              >
                Cancel
              </button>
              <button
                onClick={handleGenerate}
                disabled={generating || !prompt.trim()}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 transition-colors"
              >
                {generating ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    <span>Generate with AI</span>
                  </>
                )}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setGeneratedContent(null)}
                className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRegenerate}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center space-x-2 transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
                <span>Regenerate</span>
              </button>
              <button
                onClick={handleApprove}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2 transition-colors"
              >
                <Check className="h-4 w-4" />
                <span>Approve & Use</span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default AIContentGenerator
